import { AdminRolesEnum } from 'src/app/shared/enums/admin-roles.enum';
import { Messages } from 'src/app/shared/messages/messages';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from 'src/app/shared/service/dialog.service';
import { AuthService } from 'src/app/shared/service/auth.service';
import { LoadingService } from 'src/app/shared/service/loading.service';
import { ErrorService } from 'src/app/shared/service/error.service';
import { DOCUMENT } from '@angular/common';
import {Component, OnInit, Inject, ViewChild} from '@angular/core';
import { finalize } from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {
  IntegrationTokenConfigurationModel,
  IntegrationTokenConfigurationFilter,
  ToggleIntegrationTokenConfigurationModel, IntegrationTokenConfigurationSimpleModel
} from './models/integration-token-configuration.model';
import {MatDialog} from '@angular/material/dialog';
import {IntegrationTokenConfigurationService} from './services/integration-token-configuration.service';
import {FdAlertComponent, ModalDefinitions} from '../shared/fd-form-components/fd-alert/fd-alert.component';
import {FormIntegrationTokenConfigurationComponent} from './form/form-integration-token-configuration.component';
import {HierarchyService} from '../shared/service/hierarchy.service';
import {FdFieldConfigs} from '../shared/fd-form-components/fd-form-components.module';
import {FdSelectConfig, Item} from '../shared/fd-form-components/fd-select/fd-select.component';

@Component({
    selector: 'app-integration-token-configuration',
    templateUrl: './integration-token-configuration.component.html',
    styleUrls: ['./integration-token-configuration.component.scss'],
    standalone: false
})
export class IntegrationTokenConfigurationComponent implements OnInit {
  dataSource: MatTableDataSource<IntegrationTokenConfigurationSimpleModel> = new MatTableDataSource<IntegrationTokenConfigurationSimpleModel>();
  hasValue = false;
  hasSearched = false;

  formGroup: UntypedFormGroup;
  fields: FdFieldConfigs;

  private readonly filterAll: Item = {
    label: 'Selecione uma opção',
    value: ''
  };

  @ViewChild(MatSort) sort: MatSort;

  columnsToDisplay = ['id', 'institution', 'serviceContract', 'routingId', 'startDate', 'options_edit'];

  constructor(@Inject(DOCUMENT) private document: Document,
              private errorService: ErrorService,
              private loadingService: LoadingService,
              private configService: IntegrationTokenConfigurationService,
              private authService: AuthService,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private formBuilder: UntypedFormBuilder,
              private hierarchyService: HierarchyService) {
  }

  ngOnInit() {
    this.dataSource = new MatTableDataSource<IntegrationTokenConfigurationSimpleModel>();
    this.startForms();
    this.getInstitutionList();
    this.clearDataSource();
  }

  getInstitutionList(): void {
    this.loadingService.show();
    this.hierarchyService.institution()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(list => {
        if (list != null) {
          (this.fields.institution as FdSelectConfig).items = [];
          (this.fields.institution as FdSelectConfig).items.push(this.filterAll);
          (this.fields.institution as FdSelectConfig).items.push(...list);
        }
      });
  }

  getServiceContract(institution: string): void {
    if (!institution) {
      (this.fields.serviceContract as FdSelectConfig).items = [];
      return;
    }

    this.loadingService.show();
    this.hierarchyService.serviceContractByInstitution(institution)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(data => {
        if (data) {
          (this.fields.serviceContract as FdSelectConfig).items = [];
          (this.fields.serviceContract as FdSelectConfig).items.push(this.filterAll);
          (this.fields.serviceContract as FdSelectConfig).items.push(...data);
        }
      });
  }

  selectedInstitution(): void {
    this.formGroup.controls.serviceContract.setValue('');
    (this.fields.serviceContract as FdSelectConfig).items = [];
    this.getServiceContract(this.formGroup.value.institution);
  }

  search(): void {
    const institution = this.formGroup.value.institution;
    const serviceContract = this.formGroup.value.serviceContract;

    if (!institution || !serviceContract) {
      this.hasSearched = false;
      this.clearDataSource();
      return;
    }

    this.findConfigurationsByInstitutionAndServiceContract(institution, serviceContract);
  }

  findConfigurationsByInstitutionAndServiceContract(institution: string, serviceContract: string): void {
    this.loadingService.show();
    const filter = new IntegrationTokenConfigurationFilter();
    filter.institution = institution;
    filter.serviceContract = serviceContract;

    this.configService.getConfigurationsTokensByInstitutionAndServiceContract(filter)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(item => {
        const convertedItem = this.convertIsEnabledToBoolean(item);
        this.dataSource = new MatTableDataSource([convertedItem]);
        this.dataSource.sort = this.sort;
        this.hasValue = item != null;
      }, (err: HttpErrorResponse) => {
        const message = err.status === 404 ? Messages.SEARCH_NOT_FOUND : Messages.SEARCH_ERROR;
        this.dialog.open(FdAlertComponent, {
          disableClose: true,
          width: ModalDefinitions.DEFAULT_MODAL_WIDTH,
          data: message
        });
        this.hasSearched = true;
        this.hasValue = false;
      });
  }

  startForms(): void {
    this.formGroup = this.formBuilder.group({
      institution: [''],
      serviceContract: ['']
    });

    this.fields = {
      institution: {
        label: 'Nº Instituição',
        items: [],
        controlName: 'institution',
        panelWidth: '320px'
      },
      serviceContract: {
        label: 'Service Contract',
        items: [],
        controlName: 'serviceContract',
        panelWidth: '320px'
      }
    };
  }

  clearDataSource(): void {
    this.dataSource = new MatTableDataSource<IntegrationTokenConfigurationSimpleModel>([]);
    this.hasValue = false;
  }

  private convertIsEnabledToBoolean(item: IntegrationTokenConfigurationSimpleModel): IntegrationTokenConfigurationSimpleModel {
    return {
      ...item,
      isEnabled: typeof item.isEnabled === 'string' ? item.isEnabled === 'true' : item.isEnabled
    };
  }

  hasWriteAccess() {
    return this.authService.isUserInRoles([AdminRolesEnum.INTEGRATION_CONFIG]);
  }

  disable(id: number) {
    this.dialogService.openConfirmDialog(Messages.CONFIRM_DISABLE_ACTION_INTEGRATION_TOKEN,
      () => {
        this.setIntegrationConfigurationStatus(id, false);
      });
  }

  enable(id: number) {
    this.dialogService.openConfirmDialog(Messages.CONFIRM_ENABLE_ACTION_INTEGRATION_TOKEN,
      () => {
        this.setIntegrationConfigurationStatus(id, true);
      });
  }

  setIntegrationConfigurationStatus(id: number, isEnabled: boolean) {
    if (!id) {
      return;
    }

    const data: ToggleIntegrationTokenConfigurationModel = {id, isEnabled};

    this.loadingService.show();
    this.configService.setIntegrationTokenStatus(data)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(
        () => {
          this.dialogService.openDialog(Messages.EDIT_SAVE_SUCCESS, () => this.search());
        },
        (err: HttpErrorResponse) => { this.errorService.handleXHRError(err, Messages.SEARCH_ERROR); }
      );
  }

  addOrEditConfiguration(configuration?: IntegrationTokenConfigurationModel) {
    const dialogRef = this.dialog.open(FormIntegrationTokenConfigurationComponent, {
      width: '70%',
      height: '95%',
      data: {configuration}
    });

    dialogRef.afterClosed().subscribe(_ => {
      this.search();
    });
  }

}
